import styles from '../Home/Home.module.css';

import type { ReactNode, RefObject } from 'react';
import type { CatalogRoute } from 'src/hooks/useCatalogRoute';

import { isEmpty } from 'lodash';
import classNames from 'classnames';
import React, { useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { FunnelX, ArrowCounterClockwise } from '@phosphor-icons/react';

import { useDayFormat } from 'src/hooks/useDayFormat';
import { useCatalogRoute } from 'src/hooks/useCatalogRoute';
import { useCatalogPaging } from 'src/hooks/useCatalogPaging';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';
import { useCatalogFilterPanel } from 'src/hooks/useCatalogFilterPanel';

import { catalogHead } from 'src/utils/pageHead';
import { saleCardSlots, rentalCardSlots } from 'src/utils/listingCardSlots';
import { toSaleParams, toRentalParams, buildCatalogPath } from 'src/utils/catalogUrl';

import { useListingsPage, useListRentalListings } from 'src/queries';

import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/themed/Stack';
import { Pagination } from 'src/components/Pagination';
import { ListingCard } from 'src/components/ListingCard';
import { DocumentHead } from 'src/components/DocumentHead';
import { Typography } from 'src/components/themed/Typography';
import { VerticalSwitch } from 'src/components/VerticalSwitch';
import { ActionButton } from 'src/components/themed/ActionButton';
import { EmptyState, RequestFailureState } from 'src/components/EmptyState';
import { ListingGrid, CatalogBody, CatalogPage, ListingCount } from 'src/components/ListingGrid';

/**
 * Public dealer landing page (target of the dealer QR code): one seller's cars, for sale or for
 * rent. The address from the cabinet's QR (`/dealer/:sellerId`) opens the sale, as it always did;
 * `?vertical=rent` opens the same dealer's rentals, and the Buy / Rent switch moves between them.
 * The path is the dealer's, so a make, a model, the facets and the page ride in the query
 * (`?make=bmw&page=2&vertical=rent`), under the same rules as the catalog's own address.
 */
export const DealerHome = () => {
  const { sellerId = '' } = useParams<{ sellerId: string }>();
  const [searchParams] = useSearchParams();
  const rent = searchParams.get('vertical') === 'rent';

  return rent ? <DealerRentals sellerId={sellerId} /> : <DealerSales sellerId={sellerId} />;
};

interface DealerPageProps {
  sellerId: string;
}

/** The part both verticals share: the switch, the heading with the dealer's name, the count, the filters. */
const DealerFrame = ({
  sellerId,
  vertical,
  heading,
  dealerName,
  total,
  actions,
  sidebar,
  scrollContainer,
  children,
}: DealerPageProps & {
  vertical: 'buy' | 'rent';
  heading: string;
  dealerName?: string;
  /** The count line under the heading; `undefined` hides it (loading, a failure, an empty page). */
  total?: number;
  actions: ReactNode;
  /** The filter column on a laptop; nothing on a phone, where the funnel opens a sheet. */
  sidebar: ReactNode;
  scrollContainer: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) => (
  <div ref={scrollContainer} className={classNames('container', styles.container)}>
    <CatalogPage gap={2} p={4} pb={30}>
      <VerticalSwitch
        active={vertical}
        buyTo={buildCatalogPath({ vertical: 'buy', dealerId: sellerId })}
        rentTo={buildCatalogPath({ vertical: 'rent', dealerId: sellerId })}
      />
      <Stack width="100%" flexDirection="row" justifyContent="space-between" alignItems="center" pl={4}>
        <Stack gap={1}>
          <Typography as="h1" fontWeight="600" variant="title3" color="white">
            {heading}
          </Typography>
          {dealerName ? (
            <Typography variant="small" color="lightWhite">
              by {dealerName}
            </Typography>
          ) : null}
          {total ? <ListingCount total={total} /> : null}
        </Stack>
        <Stack flexDirection="row" gap={2}>
          {actions}
        </Stack>
      </Stack>
      <CatalogBody>
        {sidebar}
        {children}
      </CatalogBody>
    </CatalogPage>
  </div>
);

/**
 * Waiting for the lookup a make or a model in the address needs, or its failure. `onRetry` is the
 * panel's: it asks the lookup again and the lists of the filter with it.
 */
const LookupState = ({ route, onRetry }: { route: CatalogRoute; onRetry: () => void }) =>
  route.lookupState === 'loading' || route.lookupState === 'ready' ? (
    <Loader />
  ) : (
    <RequestFailureState kind={route.lookupState} onRetry={onRetry} />
  );

/** A make, a model or a facet in the address matched none of this dealer's cars. */
const NoMatch = ({ onReset }: { onReset: () => void }) => (
  <EmptyState
    icon={<FunnelX size={48} />}
    title="No cars match these filters"
    description="Try other filters, or reset them."
    action={{ label: 'Reset filters', onClick: onReset }}
  />
);

const DealerSales = ({ sellerId }: DealerPageProps) => {
  const route = useCatalogRoute('buy', { dealerId: sellerId });
  const filters = useCatalogFilterPanel('buy', route, sellerId);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const query = useListingsPage(
    // Always scope to this dealer — the seller filter is the whole point of this page.
    { ...toSaleParams(route), sellerId },
    { enabled: route.ready && !route.unknownSlug }
  );
  const { data, isRefetching, isPlaceholderData, refetch } = query;
  // The cars and the lists of the filter are asked together: one count, renewed as one.
  const reload = () => {
    refetch();
    filters.refresh();
  };
  // The dealer's QR page rendered nothing at all on a failed request (app#210).
  const state = useQueryScreenState(query);
  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;
  const dealerName = items[0]?.seller?.metadata?.firstName || items[0]?.seller?.metadata?.username;
  useCatalogPaging(route, isPlaceholderData ? undefined : data?.total, scrollContainer);

  return (
    <>
      <DocumentHead
        {...catalogHead({
          vertical: 'buy',
          dealerId: sellerId,
          dealerName,
          makeName: route.make?.name,
          modelName: route.model?.name,
          makeSlug: route.makeSlug,
          modelSlug: route.modelSlug,
          filters: route.filters,
          page: route.page,
          total: state === 'ready' && !isPlaceholderData ? data?.total : undefined,
          firstCar: items[0],
        })}
      />
      {filters.sheet}
      <DealerFrame
        sellerId={sellerId}
        vertical="buy"
        heading="Cars for sale"
        dealerName={dealerName}
        total={route.ready && !route.unknownSlug && state === 'ready' && total > 0 ? total : undefined}
        scrollContainer={scrollContainer}
        sidebar={filters.sidebar}
        actions={
          <>
            {filters.funnel}
            <ActionButton onClick={reload} label="Refresh the list">
              <ArrowCounterClockwise size={24} color="white" />
            </ActionButton>
          </>
        }
      >
        {!route.ready ? (
          <LookupState route={route} onRetry={filters.retryLookup} />
        ) : route.unknownSlug ? (
          <NoMatch onReset={filters.reset} />
        ) : state === 'loading' || (isRefetching && !isPlaceholderData) ? (
          <Loader />
        ) : state !== 'ready' ? (
          <RequestFailureState kind={state} onRetry={reload} />
        ) : !isEmpty(items) ? (
          <Stack width="100%" gap={4}>
            <ListingGrid>
              {items.map((item) => (
                <ListingCard key={item.id} {...saleCardSlots(item)} />
              ))}
            </ListingGrid>
            <Pagination total={total} page={route.page} hrefFor={(page) => route.pathTo({ page })} />
          </Stack>
        ) : route.filtered ? (
          <NoMatch onReset={filters.reset} />
        ) : (
          <Stack width="100%" alignItems="center" p={8}>
            <Typography color="lightWhite" textAlign="center">
              This dealer has no cars available right now.
            </Typography>
          </Stack>
        )}
      </DealerFrame>
    </>
  );
};

/** The dealer's rentals, a page at a time — asked of the api like the sale's, with the rent filters. */
const DealerRentals = ({ sellerId }: DealerPageProps) => {
  const route = useCatalogRoute('rent', { dealerId: sellerId });
  const filters = useCatalogFilterPanel('rent', route, sellerId);
  const dayOf = useDayFormat();
  const scrollContainer = useRef<HTMLDivElement>(null);
  const query = useListRentalListings(
    { ...toRentalParams(route), sellerId },
    { enabled: route.ready && !route.unknownSlug }
  );
  const { data, isRefetching, isPlaceholderData, refetch } = query;
  const reload = () => {
    refetch();
    filters.refresh();
  };
  const state = useQueryScreenState(query);
  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;
  const dealerName = items[0]?.seller?.metadata?.firstName || items[0]?.seller?.metadata?.username;
  useCatalogPaging(route, isPlaceholderData ? undefined : data?.total, scrollContainer);

  return (
    <>
      <DocumentHead
        {...catalogHead({
          vertical: 'rent',
          dealerId: sellerId,
          dealerName,
          makeName: route.make?.name,
          modelName: route.model?.name,
          makeSlug: route.makeSlug,
          modelSlug: route.modelSlug,
          filters: route.filters,
          page: route.page,
          total: state === 'ready' && !isPlaceholderData ? data?.total : undefined,
          firstCar: items[0],
        })}
      />
      {filters.sheet}
      <DealerFrame
        sellerId={sellerId}
        vertical="rent"
        heading="Cars for rent"
        dealerName={dealerName}
        total={route.ready && !route.unknownSlug && state === 'ready' && total > 0 ? total : undefined}
        scrollContainer={scrollContainer}
        sidebar={filters.sidebar}
        actions={
          <>
            {filters.funnel}
            <ActionButton onClick={reload} label="Refresh the list">
              <ArrowCounterClockwise size={24} color="white" />
            </ActionButton>
          </>
        }
      >
        {!route.ready ? (
          <LookupState route={route} onRetry={filters.retryLookup} />
        ) : route.unknownSlug ? (
          <NoMatch onReset={filters.reset} />
        ) : state === 'loading' || (isRefetching && !isPlaceholderData) ? (
          <Loader />
        ) : state !== 'ready' ? (
          <RequestFailureState kind={state} onRetry={reload} />
        ) : !isEmpty(items) ? (
          <Stack width="100%" gap={4}>
            <ListingGrid>
              {items.map((listing) => (
                <ListingCard key={listing.id} {...rentalCardSlots(listing, dayOf)} />
              ))}
            </ListingGrid>
            <Pagination total={total} page={route.page} hrefFor={(page) => route.pathTo({ page })} />
          </Stack>
        ) : route.filtered ? (
          <NoMatch onReset={filters.reset} />
        ) : (
          <Stack width="100%" alignItems="center" p={8}>
            <Typography color="lightWhite" textAlign="center">
              This dealer has no cars for rent right now.
            </Typography>
          </Stack>
        )}
      </DealerFrame>
    </>
  );
};
